import { rgba } from 'polished'
import React from 'react'
import styled from 'styled-components'
import IWInputLabel, { IWInputInfo } from './IWCommonLabels'

const getFocusBoxShadow = (props) => {
  const color = rgba(props.theme.palette.primary[800], 0.2)
  return `0 0 0.5rem ${color}`
}

const StyledDiv = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const StyledTextArea = styled('textarea')<{
  disabled?: boolean
}>`
  background-color: transparent;
  color: ${(props) => props.theme.palette.grey[700]};
  font-size: ${(props) => props.theme.typography.fontSizes.sm};
  border: 0;
  outline: 0;
  width: 100%;
  min-width: 1rem;
  padding: 0.5rem 1rem;
  margin: 0;

  &:disabled {
    color: ${(props) => props.theme.palette.grey[400]};
    cursor: not-allowed;
  }

  &:focus {
    outline: none !important;
  }

  ::-webkit-calendar-picker-indicator {
    display: none;
    -webkit-appearance: none;
  }
`

const StyledTextAreaWrapper = styled.div<{
  disabled?: boolean
  hasError?: boolean
}>`
  display: flex;
  gap: 0.25rem;
  align-content: center;
  align-items: center;
  background-color: ${(props) =>
    props.disabled
      ? props.theme.palette.grey[100]
      : props.theme.palette.grey[0]};
  padding: 0;
  border-radius: 0.375rem;
  border: 1px solid;
  border-color: ${(props) =>
    props.hasError && !props.disabled
      ? props.theme.palette.alert[800]
      : props.theme.palette.grey[300]};
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'default')};

  &:hover {
    border-color: ${(props) => {
      if (!props.disabled) {
        return props.hasError
          ? props.theme.palette.alert[800]
          : props.theme.palette.grey[400]
      }
      return props.theme.palette.grey[300]
    }};
  }

  ${StyledTextArea}:disabled + & {
    pointer-events: none;
  }

  &:focus-within {
    box-shadow: ${getFocusBoxShadow};
    outline: none !important;
  }

  &:focus-within {
    border-color: ${(props) =>
      props.hasError
        ? props.theme.palette.alert[800]
        : props.theme.palette.primary[700]};
    box-shadow: ${(props) => (props.hasError ? 'none' : getFocusBoxShadow)};
  }
`

type ExtraProps = {
  label?: string
  id?: string
  required?: boolean
  disabled?: boolean
  hasError?: boolean
  helperText?: string
  errorText?: string
}

type Props = ExtraProps & React.ComponentPropsWithoutRef<'textarea'>

const IWTextArea = React.forwardRef(
  (
    {
      id,
      label,
      value,
      required,
      disabled,
      hasError,
      helperText,
      errorText,
      ...props
    }: Props,
    ref: React.Ref<HTMLTextAreaElement>,
  ) => {
    return (
      <StyledDiv>
        <IWInputLabel label={label} id={id} required={required} />
        <StyledTextAreaWrapper disabled={disabled} hasError={hasError}>
          <StyledTextArea
            ref={ref}
            id={id}
            disabled={disabled}
            value={value}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...props}
          />
        </StyledTextAreaWrapper>
        <IWInputInfo helperText={helperText} errorText={errorText} />
      </StyledDiv>
    )
  },
)

export default IWTextArea
